/**
 * Admin - Release Notes Page
 *
 * Create and manage release notes that are shown to users via the WhatsNew popup.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../platform/api';
import {
  ArrowLeft,
  Megaphone,
  Plus,
  Trash2,
  Save,
  Send,
  Edit,
  X,
} from 'lucide-react';

interface ReleaseFeature {
  title: string;
  description: string;
}

interface ReleaseNote {
  id: string;
  version: string;
  title: string;
  content?: string;
  features: ReleaseFeature[];
  status: 'draft' | 'published';
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export function AdminReleaseNotesPage() {
  const navigate = useNavigate();

  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Edit/create form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formVersion, setFormVersion] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formFeatures, setFormFeatures] = useState<ReleaseFeature[]>([{ title: '', description: '' }]);

  useEffect(() => {
    loadReleaseNotes();
  }, []);

  async function loadReleaseNotes() {
    try {
      setLoading(true);
      setError(null);
      const result = await apiRequest<{ data: ReleaseNote[] }>('/api/admin/release-notes');
      const data = (result as any)?.data || result;
      if (Array.isArray(data)) {
        setReleaseNotes(data);
      } else {
        setReleaseNotes([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load release notes');
    } finally {
      setLoading(false);
    }
  }

  function startCreate() {
    setIsCreating(true);
    setEditingId(null);
    setFormVersion('');
    setFormTitle('');
    setFormFeatures([{ title: '', description: '' }]);
  }

  function startEdit(note: ReleaseNote) {
    setEditingId(note.id);
    setIsCreating(false);
    setFormVersion(note.version);
    setFormTitle(note.title);
    setFormFeatures(
      note.features.length > 0
        ? note.features.map(f => ({ ...f }))
        : [{ title: '', description: '' }]
    );
  }

  function cancelForm() {
    setIsCreating(false);
    setEditingId(null);
    setFormVersion('');
    setFormTitle('');
    setFormFeatures([{ title: '', description: '' }]);
  }

  function addFeature() {
    setFormFeatures(prev => [...prev, { title: '', description: '' }]);
  }

  function removeFeature(index: number) {
    setFormFeatures(prev => prev.filter((_, i) => i !== index));
  }

  function updateFeature(index: number, field: 'title' | 'description', value: string) {
    setFormFeatures(prev =>
      prev.map((f, i) => (i === index ? { ...f, [field]: value } : f))
    );
  }

  async function handleSave() {
    if (!formVersion.trim() || !formTitle.trim()) {
      setError('Version and title are required.');
      return;
    }

    const nonEmptyFeatures = formFeatures.filter(f => f.title.trim() || f.description.trim());

    setSaving(true);
    try {
      if (isCreating) {
        await apiRequest('/api/admin/release-notes', {
          method: 'POST',
          body: {
            version: formVersion.trim(),
            title: formTitle.trim(),
            features: nonEmptyFeatures,
          },
        });
      } else if (editingId) {
        await apiRequest(`/api/admin/release-notes/${editingId}`, {
          method: 'PATCH',
          body: {
            version: formVersion.trim(),
            title: formTitle.trim(),
            features: nonEmptyFeatures,
          },
        });
      }
      cancelForm();
      await loadReleaseNotes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save release note');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish(id: string) {
    setPublishing(id);
    try {
      await apiRequest(`/api/admin/release-notes/${id}/publish`, {
        method: 'POST',
      });
      await loadReleaseNotes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish release note');
    } finally {
      setPublishing(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Are you sure you want to delete this release note?')) return;

    setDeleting(id);
    try {
      await apiRequest(`/api/admin/release-notes/${id}`, {
        method: 'DELETE',
      });
      if (editingId === id) cancelForm();
      await loadReleaseNotes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete release note');
    } finally {
      setDeleting(null);
    }
  }

  const showForm = isCreating || editingId !== null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Megaphone className="w-6 h-6" />
                  Release Notes
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Create and manage release notes shown to users
                </p>
              </div>
            </div>
            {!showForm && (
              <button
                onClick={startCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Release Note
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <p className="text-sm text-red-600">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Edit/Create Form */}
        {showForm && (
          <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                {isCreating ? 'Create Release Note' : 'Edit Release Note'}
              </h3>
              <button
                onClick={cancelForm}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Version
                  </label>
                  <input
                    type="text"
                    value={formVersion}
                    onChange={(e) => setFormVersion(e.target.value)}
                    placeholder="e.g. 1.2.0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="What's new in this release"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Features</label>
                  <button
                    onClick={addFeature}
                    className="inline-flex items-center gap-1 text-sm text-mazo-600 hover:text-mazo-700"
                  >
                    <Plus className="w-3 h-3" />
                    Add Feature
                  </button>
                </div>
                <div className="space-y-3">
                  {formFeatures.map((feature, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={feature.title}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                          placeholder="Feature title"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent text-sm"
                        />
                        <input
                          type="text"
                          value={feature.description}
                          onChange={(e) => updateFeature(index, 'description', e.target.value)}
                          placeholder="Feature description"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {formFeatures.length > 1 && (
                        <button
                          onClick={() => removeFeature(index)}
                          className="mt-2 p-1 text-gray-400 hover:text-red-600 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={cancelForm}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-mazo-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading release notes...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && releaseNotes.length === 0 && !showForm && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Megaphone className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No release notes</h3>
            <p className="mt-1 text-sm text-gray-500">
              Create your first release note to keep users informed about updates.
            </p>
            <div className="mt-6">
              <button
                onClick={startCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Release Note
              </button>
            </div>
          </div>
        )}

        {/* Release Notes List */}
        {!loading && releaseNotes.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              {releaseNotes.map(note => (
                <div
                  key={note.id}
                  className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                    editingId === note.id ? 'bg-mazo-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        v{note.version}
                      </span>
                      <h4 className="text-sm font-medium text-gray-900">{note.title}</h4>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          note.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {note.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {new Date(note.created_at).toLocaleDateString()}
                      </span>

                      {note.status === 'draft' && (
                        <button
                          onClick={() => handlePublish(note.id)}
                          disabled={publishing === note.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                        >
                          <Send className="w-3 h-3" />
                          {publishing === note.id ? 'Publishing...' : 'Publish'}
                        </button>
                      )}

                      <button
                        onClick={() => startEdit(note)}
                        className="p-1.5 text-gray-400 hover:text-mazo-600 rounded hover:bg-gray-100 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(note.id)}
                        disabled={deleting === note.id}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {note.features.length > 0 && (
                    <div className="mt-2 ml-8">
                      <ul className="space-y-1">
                        {note.features.map((feature, i) => (
                          <li key={i} className="text-sm text-gray-600">
                            <span className="font-medium text-gray-700">{feature.title}</span>
                            {feature.description && (
                              <span className="text-gray-500"> &mdash; {feature.description}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
